// src/components/_v3/touristic-site/Hero.jsx
import React from 'react';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import mainImage from '../../../utils/touristic_sites/mainImage';
import {API_ORIGINAL_BLOB_URL} from 'utils/api';
import useTouristicSiteTranslation from '../../../hooks/touristic_sites/useTouristicSiteTranslation';
import {formatAddress, mapsUrl} from '../../../utils/touristic_sites/_v3/siteData';
import {Card, BtnBrand, Ic} from '../../../_ui/_v3/primitives';

const HeroStyled = styled.div`
  padding-block: 1.6rem 2.2rem;
`;

const HeroTopStyled = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: .6rem 1rem;
  margin-bottom: 1rem;
`;

const CatBadgeStyled = styled.span`
  display: inline-flex;
  align-items: center;
  background: ${props => props.theme.colors.paperSunk};
  border: 1px solid ${props => props.theme.colors.line};
  border-radius: 999px;
  padding: .32rem .8rem;
  font-size: .78rem;
  font-weight: 700;
  color: ${props => props.color || props.theme.colors.ink};
`;

const HeroPhotoStyled = styled(Card)`
  height: clamp(200px, 32vw, 340px);
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const HeroTitleStyled = styled.h1`
  font-size: clamp(1.9rem, 4vw, 2.8rem);
  margin: .2rem 0 .7rem;
`;

const HeroMetaStyled = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  color: ${props => props.theme.colors.inkSoft};
  font-size: .92rem;
  font-weight: 600;
  margin-bottom: 1.2rem;

  span { display: inline-flex; align-items: center; gap: .35rem; }
`;

export const PinIcon = () => (
  <Ic viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 21s7-6.2 7-11.5A7 7 0 0 0 5 9.5C5 14.8 12 21 12 21z" />
    <circle cx="12" cy="9.5" r="2.4" />
  </Ic>
);

const heroImageUrl = (site) => {
  const media = mainImage(site);
  if (!media) {
    return null;
  }
  return media.signed_id
    ? API_ORIGINAL_BLOB_URL(media.signed_id, media.filename)
    : media.url || null;
};

const Hero = ({site}) => {
  const {t} = useTranslation(['touristic-site']);
  const {title} = useTouristicSiteTranslation(site);
  const address = formatAddress(site);
  const itinerary = mapsUrl(site);
  const photo = heroImageUrl(site);
  const category = site.category || {};

  return (
    <HeroStyled>
      <HeroTopStyled>
        {category.title && <CatBadgeStyled color={category.color}>{category.title}</CatBadgeStyled>}
      </HeroTopStyled>
      {photo && (
        <HeroPhotoStyled as="div">
          <img src={photo} alt={title} />
        </HeroPhotoStyled>
      )}
      <HeroTitleStyled>{title}</HeroTitleStyled>
      {address && (
        <HeroMetaStyled>
          <span><PinIcon /> {address}</span>
        </HeroMetaStyled>
      )}
      {itinerary && (
        <BtnBrand href={itinerary} target="_blank" rel="noopener noreferrer">
          <PinIcon /> {t('v3.hero.itinerary')}
        </BtnBrand>
      )}
    </HeroStyled>
  );
};

export default Hero;
